import React, { useState } from 'react';
import {
  Flex, Box, Text, Heading, Icon, Badge, Button, HStack, VStack,
  SimpleGrid, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, useColorModeValue, Divider, Tag, Circle,
  Table, Thead, Tbody, Tr, Th, Td, Progress, Input,
} from '@chakra-ui/react';
import {
  MdMenuBook, MdBuild, MdAddTask, MdVerifiedUser, MdAdminPanelSettings, MdWater,
  MdSearch, MdEditNote, MdLocationOn, MdSend, MdSave, MdCheckCircle,
  MdCancel, MdHourglassEmpty, MdDescription, MdPictureAsPdf, MdMap,
  MdThumbUp, MdThumbDown, MdFilterList, MdPerson, MdLock, MdEdit,
  MdPhone, MdHome, MdAssignment, MdMapsHomeWork, MdArrowForward, MdPlayArrow,
} from 'react-icons/md';
import Card from 'components/card/Card.js';
import { useAuth } from '../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';

// ─── Paleta de roles ─────────────────────────────────────────────────────────
const ROLE_META = {
  tecnico: {
    label: 'Técnico',
    color: 'blue',
    accent: 'blue.500',
    bg: 'blue.50',
    bgDark: 'blue.900',
    icon: MdBuild,
    greeting: 'Bem-vindo ao SGOS!',
    sub: 'Aqui você aprende a registrar ordens de serviço, preencher formulários técnicos e acompanhar o status das suas OS.',
  },
  supervisor: {
    label: 'Supervisor',
    color: 'orange',
    accent: 'orange.500',
    bg: 'orange.50',
    bgDark: 'orange.900',
    icon: MdVerifiedUser,
    greeting: 'Bem-vindo, Supervisor!',
    sub: 'Aqui você aprende a analisar OS enviadas pelos técnicos, aprovar ou reprovar com comentários e acompanhar as equipes.',
  },
  admin: {
    label: 'Administrador',
    color: 'purple',
    accent: 'purple.500',
    bg: 'purple.50',
    bgDark: 'purple.900',
    icon: MdAdminPanelSettings,
    greeting: 'Bem-vindo, Administrador!',
    sub: 'Aqui você aprende a usar o painel completo: visualizar, editar e filtrar todas as OS, gerar relatórios em PDF e ver o mapa de atividades.',
  },
  cliente: {
    label: 'Cliente',
    color: 'teal',
    accent: 'teal.500',
    bg: 'teal.50',
    bgDark: 'teal.900',
    icon: MdWater,
    greeting: 'Bem-vindo ao Acompanhamento!',
    sub: 'Aqui você aprende a acompanhar o andamento das ordens de serviço relacionadas ao seu endereço.',
  },
};

// ─── Componentes de apoio ────────────────────────────────────────────────────

function StepBadge({ n, color }) {
  return (
    <Circle size="28px" bg={`${color}.500`} color="white" fontSize="13px" fontWeight="800" flexShrink={0}>
      {n}
    </Circle>
  );
}

function Step({ n, color, title, children }) {
  return (
    <HStack align="flex-start" spacing="12px">
      <StepBadge n={n} color={color} />
      <Box>
        <Text fontWeight="700" fontSize="sm" mb="2px">{title}</Text>
        <Text fontSize="sm" color="gray.500">{children}</Text>
      </Box>
    </HStack>
  );
}

function SectionCard({ icon, title, color, children }) {
  const bg    = useColorModeValue('white', 'navy.800');
  const hdr   = useColorModeValue(`${color}.50`, `${color}.900`);
  const txt   = useColorModeValue('navy.700', 'white');
  return (
    <AccordionItem border="1px solid" borderColor={useColorModeValue('gray.100','gray.600')} borderRadius="16px" mb="12px" overflow="hidden">
      <AccordionButton bg={hdr} _hover={{ bg: hdr, opacity: 0.9 }} py="16px" px="20px" borderRadius="0">
        <HStack flex="1" gap="12px">
          <Circle size="38px" bg={`${color}.500`} color="white">
            <Icon as={icon} boxSize="18px" />
          </Circle>
          <Text fontWeight="700" fontSize="md" color={txt}>{title}</Text>
        </HStack>
        <AccordionIcon color={`${color}.500`} />
      </AccordionButton>
      <AccordionPanel pb="20px" px="20px" bg={bg}>
        {children}
      </AccordionPanel>
    </AccordionItem>
  );
}

// ─── Mockup de tela genérico ─────────────────────────────────────────────────
function ScreenMock({ children, title }) {
  const bg      = useColorModeValue('gray.50', 'navy.900');
  const barBg   = useColorModeValue('gray.200', 'navy.700');
  return (
    <Box border="2px solid" borderColor={useColorModeValue('gray.200','gray.600')}
      borderRadius="12px" overflow="hidden" my="16px" boxShadow="sm">
      {/* barra de título simulada */}
      <Flex bg={barBg} px="12px" py="8px" gap="6px" align="center">
        <Circle size="10px" bg="red.400" />
        <Circle size="10px" bg="yellow.400" />
        <Circle size="10px" bg="green.400" />
        {title && <Text fontSize="11px" color="gray.500" ml="8px" fontFamily="mono">{title}</Text>}
      </Flex>
      <Box bg={bg} p="14px">
        {children}
      </Box>
    </Box>
  );
}

// ─── Mockup: linha de status ─────────────────────────────────────────────────
function StatusFlow({ currentStatus }) {
  const inactiveBg = useColorModeValue('gray.200', 'gray.600');
  const steps = [
    { key: 'rascunho',             label: 'Rascunho',        color: 'gray'   },
    { key: 'aguardando_validacao', label: 'Ag. Validação',   color: 'orange' },
    { key: 'aprovada',             label: 'Aprovada',        color: 'green'  },
  ];
  const idx = steps.findIndex(s => s.key === currentStatus);
  return (
    <HStack spacing="0" justify="center" py="4px">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <VStack spacing="4px">
            <Circle size="36px"
              bg={i <= idx ? `${s.color}.500` : inactiveBg}
              color="white">
              <Icon as={i <= idx ? MdCheckCircle : MdHourglassEmpty} boxSize="16px" />
            </Circle>
            <Text fontSize="10px" fontWeight={i === idx ? '700' : '400'}
              color={i <= idx ? `${s.color}.500` : 'gray.400'}>
              {s.label}
            </Text>
          </VStack>
          {i < steps.length - 1 && (
            <Box h="2px" flex={1} bg={i < idx ? 'green.300' : inactiveBg} mx="2px" mt="-16px" />
          )}
        </React.Fragment>
      ))}
    </HStack>
  );
}

// ─── Mockup: tabela de OS simplificada ───────────────────────────────────────
function MockOSTable() {
  const bg        = useColorModeValue('gray.50',  'navy.900');
  const hdr       = useColorModeValue('gray.100', 'navy.800');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const rowHover  = useColorModeValue('blue.50',  'whiteAlpha.50');
  const rows = [
    { num: '2024/001', tipo: 'Teste de Fumaça',  status: 'aprovada',             color: 'green'  },
    { num: '2024/002', tipo: 'Teste de Corante', status: 'aguardando_validacao',  color: 'orange' },
    { num: '2024/003', tipo: 'Inspeção de Rede', status: 'rascunho',              color: 'gray'   },
  ];
  const labels = { aprovada:'Aprovada', aguardando_validacao:'Ag. Validação', rascunho:'Rascunho' };
  return (
    <Box borderRadius="10px" overflow="hidden" border="1px solid" borderColor={useColorModeValue('gray.200','gray.600')}>
      <Table size="xs" variant="simple">
        <Thead bg={hdr}>
          <Tr>
            <Th fontSize="10px" py="8px">Nº OS</Th>
            <Th fontSize="10px" py="8px">Tipo de Serviço</Th>
            <Th fontSize="10px" py="8px">Status</Th>
            <Th w="20px" />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr key={r.num} _hover={{ bg: rowHover }}>
              <Td fontSize="11px" fontFamily="mono" py="8px" fontWeight="600">{r.num}</Td>
              <Td fontSize="11px" py="8px">{r.tipo}</Td>
              <Td py="8px">
                <Badge colorScheme={r.color} fontSize="9px" borderRadius="full" px="6px">{labels[r.status]}</Badge>
              </Td>
              <Td py="8px"><Icon as={MdArrowForward} color="gray.300" boxSize="14px"/></Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

// ─── Mockup: formulário de OS ─────────────────────────────────────────────────
function MockForm() {
  const inputBg = useColorModeValue('white','whiteAlpha.100');
  const lbl     = useColorModeValue('gray.600','gray.400');
  return (
    <VStack align="stretch" spacing="10px">
      <SimpleGrid columns={2} gap="8px">
        <Box>
          <Text fontSize="10px" color={lbl} mb="2px" fontWeight="600">CLIENTE</Text>
          <Box bg={inputBg} border="1px solid" borderColor="gray.200" borderRadius="8px" px="8px" py="5px">
            <Text fontSize="11px" color="gray.500">SABESP</Text>
          </Box>
        </Box>
        <Box>
          <Text fontSize="10px" color={lbl} mb="2px" fontWeight="600">Nº OS</Text>
          <Box bg={inputBg} border="1px solid" borderColor={useColorModeValue('blue.300','blue.500')} borderRadius="8px" px="8px" py="5px">
            <Text fontSize="11px" color="gray.400">Ex: 2024/00123</Text>
          </Box>
        </Box>
        <Box>
          <Text fontSize="10px" color={lbl} mb="2px" fontWeight="600">DATA</Text>
          <Box bg={inputBg} border="1px solid" borderColor="gray.200" borderRadius="8px" px="8px" py="5px">
            <Text fontSize="11px">07/04/2026</Text>
          </Box>
        </Box>
        <Box>
          <Text fontSize="10px" color={lbl} mb="2px" fontWeight="600">HORA</Text>
          <Box bg={inputBg} border="1px solid" borderColor="gray.200" borderRadius="8px" px="8px" py="5px">
            <Text fontSize="11px">08:00</Text>
          </Box>
        </Box>
      </SimpleGrid>
      <Box>
        <Text fontSize="10px" color={lbl} mb="2px" fontWeight="600">TIPO DE SERVIÇO *</Text>
        <HStack gap="4px" flexWrap="wrap">
          {['Teste de Fumaça','Teste de Corante','Inspeção de Rede'].map(t=>(
            <Badge key={t} colorScheme={t==='Teste de Fumaça'?'blue':'gray'} fontSize="9px" borderRadius="full" px="6px" cursor="pointer">
              {t}
            </Badge>
          ))}
        </HStack>
      </Box>
    </VStack>
  );
}

// ─── Mockup: botões de ação ───────────────────────────────────────────────────
function MockButtons({ locked }) {
  return (
    <HStack mt="10px" justify="flex-end" gap="8px">
      {locked
        ? <><Box px="14px" py="6px" borderRadius="8px" bg="gray.200" opacity="0.5">
              <HStack gap="4px"><Icon as={MdLock} boxSize="12px"/><Text fontSize="11px">Bloqueado</Text></HStack>
            </Box></>
        : <>
          <Box px="14px" py="6px" borderRadius="8px" border="1px solid" borderColor="gray.300">
            <HStack gap="4px"><Icon as={MdSave} boxSize="12px"/><Text fontSize="11px">Rascunho</Text></HStack>
          </Box>
          <Box px="14px" py="6px" borderRadius="8px" bg="brand.500">
            <HStack gap="4px"><Icon as={MdSend} boxSize="12px" color="white"/><Text fontSize="11px" color="white">Enviar OS</Text></HStack>
          </Box>
        </>
      }
    </HStack>
  );
}

// ─── Mockup: validação ────────────────────────────────────────────────────────
function MockValidation() {
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const rows = [
    { num: '2024/010', tec: 'Carlos Silva',   tipo: 'Teste de Fumaça'  },
    { num: '2024/011', tec: 'Ana Oliveira',   tipo: 'Teste de Corante' },
    { num: '2024/012', tec: 'Paulo Mendes',   tipo: 'Inspeção de Rede' },
  ];
  const bg  = useColorModeValue('orange.50','orange.900');
  return (
    <Box borderRadius="10px" overflow="hidden" border="1px solid" borderColor={borderCol}>
      <Box bg={bg} px="12px" py="6px">
        <Text fontSize="11px" fontWeight="700" color="orange.700">
          OS Aguardando Validação — 3 pendentes
        </Text>
      </Box>
      <Table size="xs" variant="simple">
        <Thead><Tr>
          <Th fontSize="10px" py="6px">Nº OS</Th>
          <Th fontSize="10px" py="6px">Técnico</Th>
          <Th fontSize="10px" py="6px">Tipo</Th>
          <Th w="80px"/>
        </Tr></Thead>
        <Tbody>
          {rows.map(r=>(
            <Tr key={r.num}>
              <Td fontSize="11px" fontFamily="mono" py="7px" fontWeight="600">{r.num}</Td>
              <Td fontSize="11px" py="7px">{r.tec}</Td>
              <Td fontSize="11px" py="7px">{r.tipo}</Td>
              <Td py="7px">
                <HStack gap="4px">
                  <Circle size="22px" bg="green.100" cursor="pointer">
                    <Icon as={MdThumbUp} boxSize="11px" color="green.600"/>
                  </Circle>
                  <Circle size="22px" bg="red.100" cursor="pointer">
                    <Icon as={MdThumbDown} boxSize="11px" color="red.600"/>
                  </Circle>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

// ─── Mockup: painel admin ─────────────────────────────────────────────────────
function MockAdminStats() {
  const isDark = useColorModeValue(false, true);
  const stats = [
    { label:'Total OS', value:'47', color:'blue'   },
    { label:'Aguardando', value:'8', color:'orange' },
    { label:'Aprovadas', value:'35', color:'green'  },
    { label:'Reprovadas', value:'4', color:'red'    },
  ];
  return (
    <SimpleGrid columns={4} gap="8px">
      {stats.map(s=>(
        <Box key={s.label} border="1px solid" borderColor={`${s.color}.200`}
          bg={isDark ? `${s.color}.900` : `${s.color}.50`}
          borderRadius="10px" p="10px" textAlign="center">
          <Text fontSize="18px" fontWeight="800" color={`${s.color}.500`}>{s.value}</Text>
          <Text fontSize="10px" color="gray.500">{s.label}</Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

// ─── Mockup: filtros ──────────────────────────────────────────────────────────
function MockFilters() {
  const bg = useColorModeValue('gray.100','navy.800');
  return (
    <HStack gap="8px" flexWrap="wrap">
      <HStack bg={bg} borderRadius="8px" px="8px" py="5px" gap="4px">
        <Icon as={MdSearch} boxSize="12px" color="gray.500"/>
        <Text fontSize="11px" color="gray.400">Buscar técnico, OS...</Text>
      </HStack>
      <Badge colorScheme="blue" fontSize="10px" borderRadius="full" px="8px" py="3px" cursor="pointer">Todas</Badge>
      <Badge colorScheme="orange" fontSize="10px" borderRadius="full" px="8px" py="3px" cursor="pointer">Aguardando</Badge>
      <Badge colorScheme="green" fontSize="10px" borderRadius="full" px="8px" py="3px" cursor="pointer">Aprovadas</Badge>
      <Badge colorScheme="gray" fontSize="10px" borderRadius="full" px="8px" py="3px" cursor="pointer">+ Filtros</Badge>
    </HStack>
  );
}

// ─── TIP BOX ──────────────────────────────────────────────────────────────────
function Tip({ children, icon = '💡' }) {
  return (
    <HStack align="flex-start" bg={useColorModeValue('yellow.50','yellow.900')}
      borderLeft="4px solid" borderColor="yellow.400"
      borderRadius="8px" p="12px" mt="12px" gap="8px">
      <Text fontSize="16px">{icon}</Text>
      <Text fontSize="sm" color={useColorModeValue('yellow.800','yellow.200')}>{children}</Text>
    </HStack>
  );
}

function Warn({ children }) {
  return (
    <HStack align="flex-start" bg={useColorModeValue('red.50','red.900')}
      borderLeft="4px solid" borderColor="red.400"
      borderRadius="8px" p="12px" mt="12px" gap="8px">
      <Text fontSize="16px">⚠️</Text>
      <Text fontSize="sm" color={useColorModeValue('red.800','red.200')}>{children}</Text>
    </HStack>
  );
}

// ─── CONTEÚDO POR ROLE ────────────────────────────────────────────────────────

function ConteudoTecnico({ color }) {
  const inputBgMock = useColorModeValue('white', 'whiteAlpha.100');
  const isDark      = useColorModeValue(false, true);
  return (
    <Accordion allowMultiple defaultIndex={[0]}>

      {/* 1. Minhas OS */}
      <SectionCard icon={MdBuild} title="1 — Suas Ordens de Serviço" color="blue">
        <Text fontSize="sm" mb="12px">
          Ao entrar no sistema, você já cai direto na tela <strong>Início</strong>, que mostra todas as OS que você criou. É como uma lista de tarefas!
        </Text>
        <ScreenMock title="Início — Minhas OS">
          <MockOSTable />
        </ScreenMock>
        <VStack align="stretch" spacing="10px" mt="4px">
          <Step n={1} color="blue" title="Acesse o menu lateral">
            No lado esquerdo da tela, clique em <strong>Início</strong>. No celular, use a barra na parte de baixo da tela.
          </Step>
          <Step n={2} color="blue" title="Veja suas OS">
            Cada linha é uma Ordem de Serviço. A bolinha colorida indica o status atual (cinza = rascunho, laranja = aguardando validação, verde = aprovada).
          </Step>
          <Step n={3} color="blue" title="Clique para abrir">
            Toque em qualquer OS para abrir e ver os detalhes ou continuar preenchendo.
          </Step>
        </VStack>
        <Tip>OS com o ícone de 🔒 cadeado verde já foram aprovadas e não podem ser editadas.</Tip>
      </SectionCard>

      {/* 2. Nova OS */}
      <SectionCard icon={MdAddTask} title="2 — Criar uma Nova OS" color="blue">
        <Text fontSize="sm" mb="12px">
          Para registrar um novo atendimento, vá em <strong>Registrar Atendimento</strong> no menu.
        </Text>
        <ScreenMock title="Registrar Atendimento">
          <MockForm />
          <MockButtons />
        </ScreenMock>
        <VStack align="stretch" spacing="10px" mt="4px">
          <Step n={1} color="blue" title="Preencha os dados básicos">
            Digite o <strong>Número da OS</strong> (fornecido pelo escritório), selecione a <strong>Data</strong> e a <strong>Hora</strong> do atendimento.
          </Step>
          <Step n={2} color="blue" title="Escolha o Tipo de Serviço">
            Selecione entre <em>Teste de Fumaça</em>, <em>Teste de Corante</em>, <em>Inspeção de Rede</em> ou <em>Outros</em>. Ao escolher, o formulário específico aparece abaixo.
          </Step>
          <Step n={3} color="blue" title="Preencha o formulário específico">
            Dependendo do tipo escolhido, um formulário técnico aparecerá (PVs, imóvel, localização, fotos, etc.). Veja as seções abaixo para detalhes.
          </Step>
          <Step n={4} color="blue" title="Salve ou Envie">
            Use <strong>Salvar Rascunho</strong> para continuar depois, ou <strong>Enviar OS</strong> para mandar direto para o supervisor validar.
          </Step>
        </VStack>
        <Tip>Você pode salvar como rascunho quantas vezes quiser antes de enviar. Só envie quando estiver tudo preenchido!</Tip>
      </SectionCard>

      {/* 3. Teste de Fumaça */}
      <SectionCard icon={MdMapsHomeWork} title="3 — Formulário: Teste de Fumaça / Inspeção de Rede" color="blue">
        <Text fontSize="sm" mb="12px">
          Quando o tipo de serviço for <em>Teste de Fumaça</em> ou <em>Inspeção de Rede</em>, aparece um formulário com seções para cada Poço de Visita (PV).
        </Text>
        <ScreenMock title="Formulário Técnico — Fumaça">
          <VStack align="stretch" spacing="8px">
            {/* CEP simulado */}
            <Box border="1px solid" borderColor="blue.300" borderRadius="8px" p="10px">
              <Text fontSize="10px" fontWeight="700" color="blue.500" mb="4px">📍 LOCALIZAÇÃO DO TRECHO</Text>
              <HStack gap="8px">
                <Box flex={1} bg={useColorModeValue('white','whiteAlpha.100')} border="1px solid" borderColor="gray.200" borderRadius="6px" px="8px" py="4px">
                  <Text fontSize="11px" color="gray.400">Digite o CEP...</Text>
                </Box>
                <Box bg="blue.500" borderRadius="6px" px="10px" py="5px">
                  <Text fontSize="11px" color="white">Buscar</Text>
                </Box>
                <Box border="1px solid" borderColor="green.400" borderRadius="6px" px="10px" py="5px">
                  <HStack gap="4px"><Icon as={MdLocationOn} boxSize="11px" color="green.500"/><Text fontSize="11px">GPS</Text></HStack>
                </Box>
              </HStack>
            </Box>
            {/* PVs */}
            <Box border="1px solid" borderColor="gray.200" borderRadius="8px" p="10px">
              <Text fontSize="10px" fontWeight="700" color="gray.600" mb="6px">🔵 SINGULARIDADES (PVs)</Text>
              {['PV-01','PV-02'].map((pv,i) => (
                <HStack key={pv} justify="space-between" py="4px" borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="11px" fontWeight="600">{pv}</Text>
                  <Badge colorScheme={i===0?'green':'orange'} fontSize="9px" borderRadius="full">{i===0?'Inspecionado':'Pendente'}</Badge>
                </HStack>
              ))}
              <Box mt="6px" textAlign="center">
                <Badge colorScheme="blue" fontSize="10px" cursor="pointer">+ Adicionar PV</Badge>
              </Box>
            </Box>
          </VStack>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="blue" title="Localização do trecho">
            Digite o <strong>CEP</strong> do trecho e clique em <strong>Buscar</strong>. O endereço e as coordenadas GPS são preenchidos automaticamente. Ou use o botão <strong>GPS</strong> para capturar sua posição atual.
          </Step>
          <Step n={2} color="blue" title="Singularidades (PVs)">
            Adicione um registro para cada Poço de Visita do trecho. Preencha o código, situação (tampão aberto/fechado/travado), metragem, e tire as fotos de início e fim.
          </Step>
          <Step n={3} color="blue" title="Ocorrências">
            Registre as ocorrências encontradas: galerias, bocas de lobo, imóveis. Cada ocorrência pode ter fotos e descrição.
          </Step>
          <Step n={4} color="blue" title="Fotos gerais e observações">
            Adicione fotos gerais do serviço e escreva observações importantes na seção de Observações.
          </Step>
        </VStack>
        <Tip>Tire as fotos diretamente pelo celular — use o botão de câmera para abrir a câmera do dispositivo na hora!</Tip>
      </SectionCard>

      {/* 4. Teste de Corante */}
      <SectionCard icon={MdHome} title="4 — Formulário: Teste de Corante / Inspeção de Ramal" color="teal">
        <Text fontSize="sm" mb="12px">
          Para inspeções de ramal residencial, o formulário de Teste de Corante é focado no <strong>imóvel</strong> e no cliente.
        </Text>
        <ScreenMock title="Formulário Técnico — Corante">
          <VStack align="stretch" spacing="8px">
            <Box border="1px solid" borderColor="teal.300" borderRadius="8px" p="10px">
              <Text fontSize="10px" fontWeight="700" color="teal.600" mb="6px">🏠 DADOS DO IMÓVEL</Text>
              <SimpleGrid columns={2} gap="6px">
                {['RGI','Codificação','Hidrômetro','Moradores'].map(f=>(
                  <Box key={f}>
                    <Text fontSize="9px" color="gray.500" mb="1px">{f}</Text>
                    <Box bg={inputBgMock} border="1px solid" borderColor="gray.200" borderRadius="5px" px="6px" py="3px">
                      <Text fontSize="10px" color="gray.400">...</Text>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box border="1px solid" borderColor="gray.200" borderRadius="8px" p="10px">
              <Text fontSize="10px" fontWeight="700" color="gray.600" mb="6px">👤 DADOS DO CLIENTE</Text>
              {['Nome completo','RG','Telefone'].map(f=>(
                <HStack key={f} mb="4px">
                  <Text fontSize="9px" color="gray.500" w="80px" flexShrink={0}>{f}:</Text>
                    <Box flex={1} bg={inputBgMock} border="1px solid" borderColor="gray.200" borderRadius="5px" px="6px" py="2px">
                    <Text fontSize="10px" color="gray.400">...</Text>
                  </Box>
                </HStack>
              ))}
            </Box>
          </VStack>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="teal" title="Dados do imóvel">
            Preencha o <strong>RGI</strong>, codificação, número do hidrômetro, leitura atual, número de moradores, tipo TL e categoria.
          </Step>
          <Step n={2} color="teal" title="Dados do cliente">
            Registre o nome completo, RG e telefone do responsável que assinou o termo.
          </Step>
          <Step n={3} color="teal" title="Formas de esgotamento">
            Marque as opções correspondentes ao ramal: Ligado à RCE, Factível, Potencial ou Não Factível.
          </Step>
          <Step n={4} color="teal" title="Assinatura do cliente">
            Use a área de assinatura digital — o cliente assina diretamente na tela do celular.
          </Step>
        </VStack>
        <Tip>O CEP na parte superior preenche automaticamente o endereço do imóvel!</Tip>
      </SectionCard>

      {/* 5. Status da OS */}
      <SectionCard icon={MdAssignment} title="5 — Entendendo o Status da OS" color="gray">
        <Text fontSize="sm" mb="12px">
          Cada OS passa por etapas desde a criação até a aprovação. Veja como funciona o ciclo:
        </Text>
        <ScreenMock title="Ciclo de Vida da OS">
          <StatusFlow currentStatus="aguardando_validacao" />
          <Divider my="12px" />
          <SimpleGrid columns={3} gap="8px" mt="4px">
            {[
              { color:'gray',   icon: MdEditNote,     label:'Rascunho',       desc:'OS em preenchimento. Só você vê.' },
              { color:'orange', icon: MdHourglassEmpty,label:'Ag. Validação', desc:'Enviada. Supervisor vai analisar.' },
              { color:'green',  icon: MdCheckCircle,  label:'Aprovada',       desc:'Validada! Não pode mais ser editada.' },
            ].map(s=>(
              <Box key={s.label} textAlign="center" p="8px" border="1px solid"
                borderColor={`${s.color}.200`}
                bg={isDark ? `${s.color}.900` : `${s.color}.50`}
                borderRadius="10px">
                <Circle size="32px" bg={`${s.color}.500`} color="white" mx="auto" mb="4px">
                  <Icon as={s.icon} boxSize="16px"/>
                </Circle>
                <Text fontSize="11px" fontWeight="700" color={`${s.color}.600`}>{s.label}</Text>
                <Text fontSize="10px" color="gray.500" mt="2px">{s.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </ScreenMock>
        <Warn>Se a OS for <strong>Reprovada</strong>, você receberá um comentário do supervisor explicando o que precisa ser corrigido. Edite e reenvie!</Warn>
      </SectionCard>

    </Accordion>
  );
}

// ─── CONTEÚDO SUPERVISOR ──────────────────────────────────────────────────────
function ConteudoSupervisor({ color }) {
  return (
    <Accordion allowMultiple defaultIndex={[0]}>

      <SectionCard icon={MdVerifiedUser} title="1 — Painel de Validações" color="orange">
        <Text fontSize="sm" mb="12px">
          Na tela de <strong>Validações</strong>, você vê todas as OS que os técnicos enviaram para aprovação. Você pode aprovar, reprovar ou analisar detalhadamente cada uma.
        </Text>
        <ScreenMock title="Validações">
          <MockValidation />
        </ScreenMock>
        <VStack align="stretch" spacing="10px" mt="4px">
          <Step n={1} color="orange" title="Acesse o menu Validações">
            Clique em <strong>Validações</strong> no menu lateral. O número de OS pendentes aparece ao lado do nome.
          </Step>
          <Step n={2} color="orange" title="Veja as OS aguardando">
            A tabela mostra todas as OS com status "Ag. Validação". Use a busca para filtrar por técnico ou tipo de serviço.
          </Step>
          <Step n={3} color="orange" title="Ações rápidas">
            Use o 👍 para aprovar diretamente, ou o 👎 para abrir o modal e escrever um comentário de reprovação.
          </Step>
        </VStack>
        <Tip>Prefira clicar no ícone 👁️ (olho) para analisar a OS completa antes de aprovar!</Tip>
      </SectionCard>

      <SectionCard icon={MdSearch} title="2 — Analisando uma OS em Detalhes" color="orange">
        <Text fontSize="sm" mb="12px">
          Ao clicar no ícone de análise (👁️), um painel se abre com <strong>duas abas</strong>: uma com os dados gerais e outra com todos os dados técnicos preenchidos pelo técnico.
        </Text>
        <ScreenMock title="Modal de Análise">
          <VStack align="stretch" spacing="8px">
            {/* Tabs simuladas */}
            <HStack gap="8px">
              <Box bg="orange.500" borderRadius="full" px="12px" py="4px">
                <Text fontSize="11px" color="white" fontWeight="700">📋 Detalhes</Text>
              </Box>
              <Box border="1px solid" borderColor="gray.300" borderRadius="full" px="12px" py="4px">
                <Text fontSize="11px" color="gray.500">🔬 Dados Técnicos</Text>
              </Box>
            </HStack>
            {/* Conteúdo simulado */}
            <SimpleGrid columns={3} gap="8px">
              {[['Nº OS','2024/015'],['Técnico','Carlos Silva'],['Data','07/04/2026'],['Tipo','Teste de Fumaça'],['CEP','06401-025'],['Cidade','Barueri']].map(([l,v])=>(
                <Box key={l}>
                  <Text fontSize="9px" color="gray.400" fontWeight="700" textTransform="uppercase">{l}</Text>
                  <Text fontSize="11px" fontWeight="600">{v}</Text>
                </Box>
              ))}
            </SimpleGrid>
            {/* Comentário + botões */}
            <Box mt="4px">
              <Text fontSize="10px" color="gray.400" mb="4px" fontWeight="700">COMENTÁRIO (opcional)</Text>
              <Box bg={useColorModeValue('gray.100','whiteAlpha.100')} borderRadius="8px" h="30px" />
            </Box>
            <HStack justify="flex-end" gap="8px">
              <Box px="12px" py="5px" borderRadius="8px" bg="red.500">
                <HStack gap="4px"><Icon as={MdThumbDown} boxSize="11px" color="white"/><Text fontSize="11px" color="white">Reprovar</Text></HStack>
              </Box>
              <Box px="12px" py="5px" borderRadius="8px" bg="green.500">
                <HStack gap="4px"><Icon as={MdThumbUp} boxSize="11px" color="white"/><Text fontSize="11px" color="white">Aprovar</Text></HStack>
              </Box>
            </HStack>
          </VStack>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="orange" title="Aba Detalhes">
            Confira: identificação da OS, endereço, GPS, resumo do serviço e observações do técnico.
          </Step>
          <Step n={2} color="orange" title="Aba Dados Técnicos">
            Clique na aba <strong>🔬 Dados Técnicos</strong> para ver o formulário completo preenchido pelo técnico: PVs, fotos, ocorrências, imóvel e assinatura. Isso é somente leitura.
          </Step>
          <Step n={3} color="orange" title="Decida e confirme">
            Escreva um comentário (obrigatório ao reprovar), e clique <strong>Aprovar</strong> ou <strong>Reprovar</strong>.
          </Step>
        </VStack>
        <Warn>Ao reprovar, escreva um comentário claro para que o técnico saiba exatamente o que corrigir.</Warn>
      </SectionCard>

      <SectionCard icon={MdAssignment} title="3 — Ciclo de Vida da OS" color="gray">
        <Text fontSize="sm" mb="12px">Como supervisor, você é a etapa central do processo. Veja o fluxo completo:</Text>
        <ScreenMock title="Fluxo da OS">
          <HStack justify="center" spacing="0" flexWrap="wrap" gap="4px">
            {[
              { label:'Técnico\ncria OS', color:'blue',   icon: MdBuild   },
              { label:'Envia para\nvalidação', color:'orange', icon: MdSend    },
              { label:'Você\nanalisa', color:'orange', icon: MdVerifiedUser, highlight: true },
              { label:'Aprovada\nou Reprovada', color:'green',  icon: MdCheckCircle },
            ].map((s,i)=>(
              <React.Fragment key={s.label}>
                <VStack spacing="4px" minW="70px">
                  <Circle size="36px" bg={s.highlight ? 'orange.500':'gray.200'} color={s.highlight?'white':'gray.600'}>
                    <Icon as={s.icon} boxSize="16px"/>
                  </Circle>
                  <Text fontSize="9px" textAlign="center" whiteSpace="pre-line" color={s.highlight?'orange.600':'gray.500'} fontWeight={s.highlight?'700':'400'}>{s.label}</Text>
                </VStack>
                {i < 3 && <Icon as={MdArrowForward} color="gray.300" boxSize="16px" mt="-16px"/>}
              </React.Fragment>
            ))}
          </HStack>
        </ScreenMock>
      </SectionCard>

    </Accordion>
  );
}

// ─── CONTEÚDO ADMIN ───────────────────────────────────────────────────────────
function ConteudoAdmin({ color }) {
  const inputBgMock = useColorModeValue('white', 'whiteAlpha.100');
  const greenBgMock = useColorModeValue('green.50', 'green.900');
  return (
    <Accordion allowMultiple defaultIndex={[0]}>

      <SectionCard icon={MdAdminPanelSettings} title="1 — Painel Administrativo" color="purple">
        <Text fontSize="sm" mb="12px">
          O <strong>Painel Administrativo</strong> é a central de controle do sistema. Você vê tudo: todas as OS de todos os técnicos, gráficos, mapa e KPIs.
        </Text>
        <ScreenMock title="Painel Administrativo">
          <MockAdminStats />
          <Box mt="10px">
            <Progress value={74} colorScheme="green" size="sm" borderRadius="full" />
            <Text fontSize="9px" color="gray.500" mt="2px">Taxa de aprovação: 74%</Text>
          </Box>
        </ScreenMock>
        <VStack align="stretch" spacing="10px" mt="4px">
          <Step n={1} color="purple" title="Cards de resumo">
            No topo você vê: Total de OS, Aguardando, Aprovadas, Reprovadas, Taxa de aprovação e Técnicos ativos. Clique em um card para filtrar a tabela por aquele status.
          </Step>
          <Step n={2} color="purple" title="Gráficos">
            Role a página para ver o gráfico de pizza dos status, o gráfico de barras por técnico e o gráfico de linha mostrando OS criadas nos últimos 30 dias.
          </Step>
          <Step n={3} color="purple" title="Mapa">
            No final da página, há um mapa interativo com um pin por OS. Cores: verde = aprovada, laranja = aguardando, vermelho = reprovada.
          </Step>
        </VStack>
      </SectionCard>

      <SectionCard icon={MdFilterList} title="2 — Filtrar e Buscar OS" color="purple">
        <Text fontSize="sm" mb="12px">
          Acima da tabela existem ferramentas de filtro para encontrar qualquer OS rapidamente.
        </Text>
        <ScreenMock title="Filtros">
          <MockFilters />
        </ScreenMock>
        <VStack align="stretch" spacing="10px" mt="4px">
          <Step n={1} color="purple" title="Busca livre">
            Digite na caixa de busca o nome do técnico, nº da OS, tipo de serviço, endereço ou cidade.
          </Step>
          <Step n={2} color="purple" title="Filtro por status">
            Clique nos badges coloridos para filtrar: Todas, Rascunho, Enviadas, Ag. Validação, Aprovadas ou Reprovadas.
          </Step>
          <Step n={3} color="purple" title="Período e técnico">
            Use os campos de data (De / Até) e o seletor de técnico para refinar ainda mais.
          </Step>
          <Step n={4} color="purple" title="Ordenação">
            Clique no cabeçalho de qualquer coluna da tabela para ordenar crescente ou decrescente.
          </Step>
        </VStack>
        <Tip>Clique em "Limpar filtros" para voltar a ver todas as OS de uma vez.</Tip>
      </SectionCard>

      <SectionCard icon={MdEdit} title="3 — Editar uma OS" color="purple">
        <Text fontSize="sm" mb="12px">
          Como administrador, você pode editar qualquer OS — mesmo as aprovadas — sem restrições.
        </Text>
        <ScreenMock title="Editar OS">
          <VStack align="stretch" spacing="8px">
            <HStack gap="8px">
              {['📋 Detalhes','🔬 Dados Técnicos'].map((t,i)=>(
                <Box key={t} bg={i===0?'purple.500':'transparent'} border={i===0?'none':'1px solid'} borderColor="gray.300" borderRadius="full" px="12px" py="4px">
                  <Text fontSize="11px" color={i===0?'white':'gray.500'} fontWeight={i===0?'700':'400'}>{t}</Text>
                </Box>
              ))}
            </HStack>
            <SimpleGrid columns={2} gap="8px">
              {[['Status','Aprovada ✓'],['Nº OS','2024/015'],['Tipo','Teste de Fumaça ▼'],['Hora','08:30']].map(([l,v])=>(
                <Box key={l}>
                  <Text fontSize="9px" color="gray.400" fontWeight="700">{l}</Text>
                  <Box bg={inputBgMock} border="1px solid" borderColor="gray.200" borderRadius="5px" px="6px" py="3px" mt="1px">
                    <Text fontSize="11px">{v}</Text>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
            <HStack justify="flex-end">
              <Box px="12px" py="5px" borderRadius="8px" bg="brand.500">
                <Text fontSize="11px" color="white">Salvar Alterações</Text>
              </Box>
            </HStack>
          </VStack>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="purple" title="Clique no ícone de lápis ✏️">
            Na tabela, cada OS tem um botão de edição. Clique nele para abrir o painel lateral de edição.
          </Step>
          <Step n={2} color="purple" title="Aba Detalhes">
            Altere o status, nº da OS, tipo de serviço, hora, observações e coordenadas GPS manualmente.
          </Step>
          <Step n={3} color="purple" title="Aba Dados Técnicos">
            Edite os dados do formulário técnico completo (PVs, imóvel, fotos, etc.).
          </Step>
          <Step n={4} color="purple" title="Salvar">
            Clique em <strong>Salvar Alterações</strong>. As mudanças são aplicadas instantaneamente.
          </Step>
        </VStack>
      </SectionCard>

      <SectionCard icon={MdPictureAsPdf} title="4 — Gerar Relatório PDF" color="green">
        <Text fontSize="sm" mb="12px">
          O sistema gera relatórios ABNT personalizados por tipo de OS. O PDF é gerado direto no navegador e pode ser impresso ou salvo.
        </Text>
        <ScreenMock title="Seletor de Relatório">
          <VStack align="stretch" spacing="8px">
            <Text fontSize="11px" fontWeight="700">Selecione a empresa contratante:</Text>
            <HStack gap="8px">
              {['PG Soluções','Norbrasil'].map((e,i)=>(
                <Box key={e} flex={1} textAlign="center" border="2px solid"
                  borderColor={i===0?'green.400':'gray.200'}
                  bg={i===0 ? greenBgMock : 'transparent'}
                  borderRadius="10px" p="10px" cursor="pointer">
                  <Text fontSize="11px" fontWeight={i===0?'700':'400'}>{e}</Text>
                  {i===0 && <Badge colorScheme="green" fontSize="9px" mt="2px">Selecionado</Badge>}
                </Box>
              ))}
            </HStack>
            <Box bg="green.500" borderRadius="8px" px="12px" py="6px" textAlign="center">
              <HStack justify="center" gap="4px">
                <Icon as={MdPictureAsPdf} color="white" boxSize="14px"/>
                <Text fontSize="11px" color="white" fontWeight="700">Gerar PDF</Text>
              </HStack>
            </Box>
          </VStack>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="green" title="Clique no ícone de relatório 📄">
            Na tabela, cada OS tem um botão verde de relatório. Clique nele.
          </Step>
          <Step n={2} color="green" title="Escolha a empresa">
            Selecione <strong>PG Soluções</strong> ou <strong>Norbrasil</strong> conforme o contrato da OS.
          </Step>
          <Step n={3} color="green" title="PDF abre automaticamente">
            Uma nova janela abre com o relatório formatado. Use <strong>Ctrl+P</strong> (ou o menu do navegador) para imprimir ou salvar como PDF.
          </Step>
        </VStack>
        <Tip>OS do tipo Teste de Fumaça e Teste de Corante geram relatórios especializados com layout diferente!</Tip>
      </SectionCard>

      <SectionCard icon={MdMap} title="5 — Mapa Interativo de OS" color="blue">
        <Text fontSize="sm" mb="12px">
          O mapa no final do painel mostra a localização de cada OS com coordenadas GPS cadastradas.
        </Text>
        <ScreenMock title="Mapa de OS">
          <Box h="80px" bg={useColorModeValue('blue.50','navy.900')} borderRadius="8px" position="relative" overflow="hidden">
            <Text fontSize="10px" color="gray.400" position="absolute" top="4px" left="8px">🗺️ Mapa interativo</Text>
            {[
              { top:'30px', left:'40px',  color:'green'  },
              { top:'15px', left:'100px', color:'orange' },
              { top:'50px', left:'160px', color:'green'  },
              { top:'25px', left:'210px', color:'red'    },
            ].map((p,i)=>(
              <Box key={i} position="absolute" top={p.top} left={p.left}>
                <Icon as={MdLocationOn} boxSize="18px" color={`${p.color}.500`}/>
              </Box>
            ))}
          </Box>
        </ScreenMock>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="blue" title="Cores dos pins">
            Verde = aprovada, Laranja = aguardando validação, Azul = enviada/rascunho, Vermelho = reprovada.
          </Step>
          <Step n={2} color="blue" title="Clique em um pin">
            Aparece um pop-up com o resumo da OS (técnico, tipo, status). Clique em <strong>Ver OS</strong> para abrir no painel.
          </Step>
          <Step n={3} color="blue" title="Centralizar no mapa">
            Use o botão <strong>Centralizar</strong> para ajustar o mapa para mostrar todos os pins de uma vez.
          </Step>
        </VStack>
        <Tip>OS sem coordenadas GPS aparecem na contagem abaixo do mapa. Use o botão ✏️ para adicionar coordenadas manualmente.</Tip>
      </SectionCard>

    </Accordion>
  );
}

// ─── CONTEÚDO CLIENTE ─────────────────────────────────────────────────────────
function ConteudoCliente({ color }) {
  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <SectionCard icon={MdWater} title="1 — Acompanhamento de OS" color="teal">
        <Text fontSize="sm" mb="12px">
          Nesta tela você pode acompanhar o andamento das ordens de serviço relacionadas ao seu endereço.
        </Text>
        <VStack align="stretch" spacing="10px">
          <Step n={1} color="teal" title="Visualize suas OS">
            Ao entrar no sistema, você vê automaticamente as OS vinculadas ao seu cadastro.
          </Step>
          <Step n={2} color="teal" title="Status da OS">
            O status indica em que fase está o serviço: em andamento, aguardando aprovação ou concluído.
          </Step>
          <Step n={3} color="teal" title="Dúvidas?">
            Em caso de dúvidas sobre uma OS específica, entre em contato com a equipe de atendimento.
          </Step>
        </VStack>
      </SectionCard>
    </Accordion>
  );
}

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
export default function Manual() {
  const { user, role } = useAuth();
  const navigate       = useNavigate();

  const textColor = useColorModeValue('navy.700', 'white');
  const subText   = useColorModeValue('gray.500', 'gray.400');
  const cardBg    = useColorModeValue('white', 'navy.800');

  const r    = role || 'tecnico';
  const meta = ROLE_META[r] || ROLE_META.tecnico;

  const ctaLink = {
    tecnico:    '/admin/tecnico/registrar',
    supervisor: '/admin/validacoes',
    admin:      '/admin/admin-panel',
    cliente:    '/admin/cliente/acompanhamento',
  }[r] || '/admin';

  const heroBg   = useColorModeValue(meta.bg, meta.bgDark);

  return (
    <Flex direction="column" pt={{ base: '8px', md: '16px', xl: '0px' }} pb="40px">

      {/* ── Hero ── */}
      <Card bg={heroBg} border="2px solid" borderColor={`${meta.color}.200`} mb="24px" p={{ base:'20px', md:'32px' }}>
        <Flex align={{ base:'flex-start', md:'center' }} gap="20px" direction={{ base:'column', md:'row' }}>
          <Circle size={{ base:'60px', md:'80px' }} bg={`${meta.color}.500`} flexShrink={0}>
            <Icon as={meta.icon} boxSize={{ base:'28px', md:'38px' }} color="white" />
          </Circle>
          <Box flex={1}>
            <HStack mb="4px" flexWrap="wrap" gap="8px">
              <Heading size="lg" color={textColor}>{meta.greeting}</Heading>
              <Badge colorScheme={meta.color} fontSize="12px" borderRadius="full" px="10px" py="2px">
                {meta.label}
              </Badge>
            </HStack>
            <Text color={subText} fontSize={{ base:'sm', md:'md' }}>{meta.sub}</Text>
          </Box>
          <Button
            colorScheme={meta.color}
            leftIcon={<Icon as={MdPlayArrow} />}
            borderRadius="12px"
            h="44px" px="20px"
            flexShrink={0}
            onClick={() => navigate(ctaLink)}
          >
            Começar a usar
          </Button>
        </Flex>
      </Card>

      {/* ── Ciclo rápido (badge visual) — exceto cliente ── */}
      {r !== 'cliente' && (
        <Card bg={cardBg} mb="24px" p={{ base:'16px', md:'24px' }}>
          <Text fontSize="sm" fontWeight="700" color={textColor} mb="12px">
            📌 Fluxo geral do sistema
          </Text>
          <HStack flexWrap="wrap" gap="6px" justify="center">
            {[
              { label:'Técnico cria OS',          color:'blue'   },
              { label:'→', color:'gray', isArrow:true           },
              { label:'Envia para validação',      color:'blue'   },
              { label:'→', color:'gray', isArrow:true           },
              { label:'Supervisor analisa',        color:'orange' },
              { label:'→', color:'gray', isArrow:true           },
              { label:'Aprovada ✓',               color:'green'  },
            ].map((s,i) => s.isArrow
              ? <Text key={i} color="gray.400" fontSize="lg" fontWeight="300">{s.label}</Text>
              : <Badge key={i} colorScheme={s.color} fontSize="11px" borderRadius="full" px="10px" py="4px">{s.label}</Badge>
            )}
          </HStack>
          <Text fontSize="xs" color={subText} textAlign="center" mt="8px">
            Admin pode editar e gerar relatórios em qualquer etapa.
          </Text>
        </Card>
      )}

      {/* ── Conteúdo por role ── */}
      {r === 'tecnico'    && <ConteudoTecnico    color={meta.color} />}
      {r === 'supervisor' && <ConteudoSupervisor color={meta.color} />}
      {r === 'admin'      && <ConteudoAdmin      color={meta.color} />}
      {r === 'cliente'    && <ConteudoCliente    color={meta.color} />}

      {/* ── Rodapé ── */}
      <Box textAlign="center" mt="32px">
        <Divider mb="20px" />
        <Icon as={MdMenuBook} boxSize="24px" color={subText} />
        <Text fontSize="sm" color={subText} mt="4px">
          SGOS — Sistema de Gestão de Ordens de Serviço
        </Text>
        <Text fontSize="xs" color={subText} mt="2px">
          PG Soluções em Saneamento • Manual do Usuário
        </Text>
      </Box>

    </Flex>
  );
}
